import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getAuthHeaders } from '../../utils/auth';
import { formatUsDateFromDateTime, formatUsTimeFromDateTime } from '../../utils/usDateFormat';
import Loader from '../../components/Loader/Loader';
import ReusablePopup from '../../components/Modals/ReuseableModal';
import Pagination from '../../components/Pagination/Pagination';
import {
  POLICY_TRAINING_ACCEPT,
  POLICY_TRAINING_MAX_FILES,
  POLICY_TRAINING_TYPES,
  formatPolicyTrainingFileSize,
  isPolicyTrainingInAppViewable,
  normalizePolicyTrainingAttachments,
  parseApiErrorDetail,
  validatePolicyTrainingFiles,
  canManagePolicyTraining,
  canDeletePolicyTraining,
  canDownloadPolicyTraining,
  canViewDeletedPolicyTraining,
  canRestorePolicyTraining,
  downloadPolicyTrainingAttachment,
} from '../../utils/policyTrainingUpload';
import DepartmentPositionScopePicker from '../../components/ScopePicker/DepartmentPositionScopePicker';
import PolicyTrainingViewerModal from '../../components/Modals/PolicyTrainingViewerModal';
import {
  fetchPolicyTrainingById,
  parseNotificationActionError,
} from '../../utils/notificationNavigation';

const PAGE_SIZE = 10;
const TAB_OPTIONS = [
  { key: 'policy', label: 'Policies' },
  { key: 'training', label: 'Trainings' },
  { key: 'hr_form', label: 'HR Forms' },
];
const DELETED_TAB = { key: 'deleted', label: 'Deleted' };

const typeLabel = (documentType) => {
  if (documentType === 'training') return 'Training';
  if (documentType === 'hr_form') return 'HR Form';
  return 'Policy';
};

const numberFieldLabel = (documentType) => {
  if (documentType === 'training') return 'Training Number';
  if (documentType === 'hr_form') return 'Form Number';
  return 'Policy Number';
};

const numberFieldPlaceholder = (documentType) => {
  if (documentType === 'training') return 'e.g. TRN-2025-01';
  if (documentType === 'hr_form') return 'e.g. FRM-2025-01';
  return 'e.g. POL-2025-01';
};

const formatScopeLabel = (items, allLabel) =>
  Array.isArray(items) && items.length > 0 ? items.join(', ') : allLabel;

const formatScopeSubtitle = (doc) => {
  const depts = doc?.departments || [];
  const positions = doc?.positions || [];
  if (depts.length === 0 && positions.length === 0) return 'All staff';
  const deptPart = depts.length > 0 ? depts.join(', ') : 'All departments';
  const posPart = positions.length > 0 ? positions.join(', ') : 'All positions';
  return `${deptPart} · ${posPart}`;
};

const CreatedAtDisplay = ({ value, timeClassName = 'text-xs text-gray-400' }) => {
  const date = formatUsDateFromDateTime(value);
  const time = formatUsTimeFromDateTime(value);
  if (!date) return '—';
  return (
    <span className="inline-flex flex-col">
      <span>{date}</span>
      {time && <span className={timeClassName}>{time}</span>}
    </span>
  );
};

// ── File type icon ────────────────────────────────────────────────────────────
const FileIcon = ({ contentType }) => {
  const t = (contentType || '').toLowerCase();
  if (t.startsWith('video/')) {
    return (
      <svg className="w-5 h-5 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.723v6.554a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    );
  }
  if (t === 'application/pdf') {
    return (
      <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    );
  }
  if (t.startsWith('image/')) {
    return (
      <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    );
  }
  if (t.includes('presentation') || t.includes('powerpoint')) {
    return (
      <svg className="w-5 h-5 text-orange-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
      </svg>
    );
  }
  return (
    <svg className="w-5 h-5 text-[#674936] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.414a4 4 0 10-5.656-5.656L4.343 11.172a6 6 0 108.485 8.485L20 12.5" />
    </svg>
  );
};

const fileTypeBadge = (contentType) => {
  const t = (contentType || '').toLowerCase();
  if (t.startsWith('video/')) return { label: 'Video', className: 'bg-blue-100 text-blue-700' };
  if (t === 'application/pdf') return { label: 'PDF', className: 'bg-red-100 text-red-700' };
  if (t.startsWith('image/')) return { label: 'Image', className: 'bg-green-100 text-green-700' };
  if (t.includes('presentation') || t.includes('powerpoint'))
    return { label: 'PPT', className: 'bg-orange-100 text-orange-700' };
  if (t.includes('word')) return { label: 'Word', className: 'bg-indigo-100 text-indigo-700' };
  return { label: 'File', className: 'bg-gray-100 text-gray-700' };
};

const AttachmentRow = ({ att, canDownload, allowInAppDownload, onViewInApp, onDownload }) => {
  const fileUrl = att.file_url || att.fileUrl;
  const fileName = att.file_name || att.fileName || 'File';
  const badge = fileTypeBadge(att.content_type);
  const inApp = isPolicyTrainingInAppViewable(att.content_type);
  const [downloading, setDownloading] = useState(false);
  const showDownload = canDownload && fileUrl && (allowInAppDownload || !inApp);

  const handleDownload = async () => {
    if (!onDownload || downloading) return;
    setDownloading(true);
    try {
      await onDownload(att);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-2 min-w-0">
          <FileIcon contentType={att.content_type} />
          <div className="min-w-0">
            <p className="text-gray-800 font-medium break-all text-sm">{fileName}</p>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${badge.className}`}>
                {badge.label}
              </span>
              {att.size != null && (
                <span className="text-xs text-gray-500">{formatPolicyTrainingFileSize(att.size)}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0 border-t border-gray-200 pt-2 sm:border-0 sm:pt-0">
          {fileUrl && inApp && (
            <button
              type="button"
              onClick={() => onViewInApp(att)}
              className="text-[#674936] hover:underline text-sm font-semibold touch-manipulation"
            >
              View
            </button>
          )}
          {fileUrl && !inApp && (
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#674936] hover:underline text-sm font-semibold touch-manipulation"
            >
              View
            </a>
          )}
          {showDownload && (
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="text-[#674936] hover:underline text-sm font-semibold touch-manipulation disabled:opacity-60"
            >
              {downloading ? 'Downloading…' : 'Download'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Detail modal ──────────────────────────────────────────────────────────────
const DocumentDetailModal = ({ doc, onClose, onEdit, onDelete, canEdit, canDelete, role, onViewInApp, onDownloadError }) => {
  if (!doc) return null;
  const attachments = normalizePolicyTrainingAttachments(doc);
  const canDownload = canDownloadPolicyTraining(role, doc.document_type);
  const allowInAppDownload = doc.document_type === 'hr_form';

  const handleDownload = async (att) => {
    const fileUrl = att.file_url || att.fileUrl;
    const fileName = att.file_name || att.fileName || 'download';
    try {
      await downloadPolicyTrainingAttachment(fileUrl, fileName);
    } catch (err) {
      if (onDownloadError) {
        onDownloadError(parseApiErrorDetail(err) || 'Failed to download file. Please try again.');
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 overflow-y-auto overscroll-contain py-4 px-3 sm:items-center sm:p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[min(90vh,calc(100dvh-2rem))] flex flex-col min-w-0 overflow-hidden my-auto">
        <div className="flex justify-between items-start gap-3 p-4 sm:p-6 pb-0 shrink-0">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 break-words">
              {doc.title}
            </h2>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              {doc.document_number && (
                <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                  #{doc.document_number}
                </span>
              )}
              <span className="inline-block text-xs font-medium px-2 py-0.5 rounded bg-[#674936]/10 text-[#674936]">
                {typeLabel(doc.document_type)}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-gray-400 hover:text-gray-600 transition-colors touch-manipulation p-1"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain touch-pan-y px-4 sm:px-6 py-4">
        {doc.description && (
          <p className="text-gray-600 mb-4 whitespace-pre-wrap break-words text-sm sm:text-base">
            {doc.description}
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-gray-500 mb-5">
          <div>
            <span className="font-semibold text-gray-700">{numberFieldLabel(doc.document_type)}:</span>{' '}
            {doc.document_number || '—'}
          </div>
          <div>
            <span className="font-semibold text-gray-700">Uploaded:</span>{' '}
            <CreatedAtDisplay value={doc.created_at} />
          </div>
          <div>
            <span className="font-semibold text-gray-700">Uploaded by:</span>{' '}
            {doc.uploaded_by_name || '—'}
          </div>
          <div>
            <span className="font-semibold text-gray-700">Departments:</span>{' '}
            {formatScopeLabel(doc.departments, 'All departments')}
          </div>
          <div>
            <span className="font-semibold text-gray-700">Positions:</span>{' '}
            {formatScopeLabel(doc.positions, 'All positions')}
          </div>
        </div>

        {attachments.length > 0 && (
          <div className="space-y-3 mb-5">
            <h3 className="text-sm font-semibold text-gray-700">
              Attachments ({attachments.length})
            </h3>
            {attachments.map((att) => (
              <AttachmentRow
                key={att.id || att.file_url || att.fileUrl}
                att={att}
                canDownload={canDownload}
                allowInAppDownload={allowInAppDownload}
                onViewInApp={onViewInApp}
                onDownload={handleDownload}
              />
            ))}
          </div>
        )}

        </div>

        <div className="shrink-0 p-4 sm:p-6 pt-0 flex flex-col-reverse sm:flex-row sm:justify-between gap-3 border-t border-gray-100">
          {(canEdit || canDelete) && (
            <div className="flex flex-wrap gap-2">
              {canEdit && (
                <button
                  type="button"
                  onClick={() => onEdit(doc)}
                  className="px-4 py-2 bg-[#674936] text-white rounded-lg hover:bg-[#503220] transition-colors font-semibold text-sm touch-manipulation"
                >
                  Edit
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  onClick={() => onDelete(doc)}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-semibold text-sm touch-manipulation"
                >
                  Delete
                </button>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-semibold text-sm touch-manipulation sm:ml-auto"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Document list card ────────────────────────────────────────────────────────
const DocumentCard = ({ doc, onClick, showScope = false }) => {
  const attachments = normalizePolicyTrainingAttachments(doc);
  const first = attachments[0] || {};
  const badge = fileTypeBadge(first.content_type);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick(doc)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(doc);
        }
      }}
      className="w-full text-left bg-white border border-gray-200 rounded-lg p-3 sm:p-4 hover:border-[#674936] hover:shadow-sm transition-all duration-200 cursor-pointer"
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5">
          <FileIcon contentType={first.content_type} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            {doc.document_number && (
              <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 shrink-0">
                #{doc.document_number}
              </span>
            )}
            <span className="font-semibold text-gray-800 text-sm truncate">{doc.title}</span>
            {first.content_type && (
              <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${badge.className}`}>
                {badge.label}
              </span>
            )}
            {attachments.length > 1 && (
              <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                {attachments.length} files
              </span>
            )}
          </div>
          {doc.description && (
            <p className="text-xs text-gray-500 line-clamp-2 mb-1">{doc.description}</p>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-400 items-end">
            <CreatedAtDisplay value={doc.created_at} timeClassName="text-[10px] text-gray-400" />
            {doc.uploaded_by_name && <span>By: {doc.uploaded_by_name}</span>}
            {showScope && (
              <span className="text-gray-500">{formatScopeSubtitle(doc)}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DeletedDocumentCard = ({ doc, onRestore, restoring = false }) => {
  const attachments = normalizePolicyTrainingAttachments(doc);
  const first = attachments[0] || {};
  const badge = fileTypeBadge(first.content_type);
  const docId = doc.id || doc.original_document_id;

  return (
    <div className="w-full bg-white border border-gray-200 rounded-lg p-3 sm:p-4">
      <div className="flex items-start gap-3">
        <div className="shrink-0 mt-0.5">
          <FileIcon contentType={first.content_type} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            {doc.document_number && (
              <span className="text-xs font-medium px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 shrink-0">
                #{doc.document_number}
              </span>
            )}
            <span className="font-semibold text-gray-800 text-sm truncate">{doc.title}</span>
            <span className="inline-block text-xs font-medium px-1.5 py-0.5 rounded bg-[#674936]/10 text-[#674936]">
              {typeLabel(doc.document_type)}
            </span>
            {first.content_type && (
              <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${badge.className}`}>
                {badge.label}
              </span>
            )}
          </div>
          {doc.description && (
            <p className="text-xs text-gray-500 line-clamp-2 mb-1">{doc.description}</p>
          )}
          <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-400 items-end mb-3">
            {doc.deleted_at && (
              <span>
                Deleted: <CreatedAtDisplay value={doc.deleted_at} timeClassName="text-[10px] text-gray-400" />
              </span>
            )}
            {doc.deleted_by_name && <span>By: {doc.deleted_by_name}</span>}
            {doc.expires_at && (
              <span>
                Expires: <CreatedAtDisplay value={doc.expires_at} timeClassName="text-[10px] text-gray-400" />
              </span>
            )}
          </div>
          <button
            type="button"
            disabled={restoring}
            onClick={() => onRestore(docId)}
            className="bg-[#674936] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#563a2e] disabled:opacity-60 touch-manipulation"
          >
            {restoring ? 'Restoring…' : 'Restore'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Upload modal (admin / super) ────────────────────────────────────────────
const UploadDocumentModal = ({ activeTab, onClose, onSuccess, onError, allowed }) => {
  const [documentNumber, setDocumentNumber] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [documentType, setDocumentType] = useState(activeTab);
  const [files, setFiles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    setDocumentType(activeTab);
  }, [activeTab]);

  const handleFileChange = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (picked.length === 0) return;
    const combined = [...files, ...picked];
    const error = validatePolicyTrainingFiles(combined);
    if (error) {
      setFieldError(error);
      return;
    }
    setFieldError('');
    setFiles(combined);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setFieldError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allowed) {
      onError('Only admins can upload documents.');
      return;
    }
    const titleTrimmed = title.trim();
    const numberTrimmed = documentNumber.trim();
    if (!numberTrimmed) { setFieldError(`${numberFieldLabel(documentType)} is required.`); return; }
    if (!titleTrimmed) { setFieldError('Title is required.'); return; }
    const validationError = validatePolicyTrainingFiles(files);
    if (validationError) { setFieldError(validationError); return; }
    setFieldError('');
    setSubmitting(true);
    try {
      const headers = await getAuthHeaders();
      const formData = new FormData();
      formData.append('document_number', numberTrimmed);
      formData.append('title', titleTrimmed);
      formData.append('description', description.trim());
      formData.append('document_type', documentType);
      formData.append('departments', departments.join(','));
      formData.append('positions', positions.join(','));
      files.forEach((file) => formData.append('files', file, file.name));
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/policies-trainings`,
        formData,
        { headers, validateStatus: (s) => s >= 200 && s < 300 },
      );
      setDocumentNumber('');
      setTitle('');
      setDescription('');
      setFiles([]);
      setDepartments([]);
      setPositions([]);
      setDocumentType(activeTab);
      onSuccess('Document uploaded successfully!');
      onClose();
    } catch (err) {
      console.error('Upload failed:', err);
      onError(parseApiErrorDetail(err) || 'Failed to upload document. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!allowed) return null;

  return (
    <div className="modal !items-start sm:!items-center !py-4 sm:!py-6 overflow-y-auto">
      <div className="modal-content !p-4 sm:!p-6 max-h-[min(90vh,calc(100dvh-2rem))] overflow-y-auto overscroll-contain w-full max-w-[min(1000px,calc(100vw-2rem))] my-auto shadow-xl">
        <h3 className="w-full flex justify-center items-center text-center px-1 mb-4">
          Upload New Document
        </h3>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type <span className="text-red-500">*</span>
          </label>
          <select
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#674936] focus:border-transparent"
            disabled={submitting}
          >
            {POLICY_TRAINING_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {numberFieldLabel(documentType)} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={documentNumber}
            onChange={(e) => setDocumentNumber(e.target.value)}
            placeholder={numberFieldPlaceholder(documentType)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#674936] focus:border-transparent"
            maxLength={50}
            disabled={submitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Attendance Policy 2025"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#674936] focus:border-transparent"
            maxLength={200}
            disabled={submitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="Optional brief description"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#674936] focus:border-transparent resize-none"
            maxLength={1000}
            disabled={submitting}
          />
        </div>
        <DepartmentPositionScopePicker
          selectedDepartments={departments}
          selectedPositions={positions}
          onDepartmentsChange={setDepartments}
          onPositionsChange={setPositions}
          disabled={submitting}
          onLoadError={onError}
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Files <span className="text-red-500">*</span>
          </label>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={POLICY_TRAINING_ACCEPT}
                onChange={handleFileChange}
                className="hidden"
                disabled={submitting || files.length >= POLICY_TRAINING_MAX_FILES}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={submitting || files.length >= POLICY_TRAINING_MAX_FILES}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm rounded-lg border border-gray-300 font-medium transition-colors touch-manipulation disabled:opacity-50"
              >
                Choose Files
              </button>
              <span className="text-xs text-gray-400">
                Up to {POLICY_TRAINING_MAX_FILES} files — video, PDF, images, Word, or PowerPoint (100 MB each)
              </span>
            </div>
            {files.length > 0 && (
              <ul className="space-y-2 mt-1">
                {files.map((file, idx) => (
                  <li
                    key={`${file.name}-${idx}`}
                    className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm"
                  >
                    <FileIcon contentType={file.type} />
                    <span className="truncate flex-1 min-w-0" title={file.name}>{file.name}</span>
                    <span className="text-xs text-gray-400 shrink-0">{formatPolicyTrainingFileSize(file.size)}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(idx)}
                      className="text-gray-400 hover:text-red-500 transition-colors touch-manipulation"
                      aria-label="Remove file"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
        {fieldError && <p className="text-red-600 text-sm">{fieldError}</p>}
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end sm:gap-4 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="w-full sm:w-auto px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-300 touch-manipulation disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:w-auto px-6 py-3 bg-[#674936] text-white rounded-lg hover:bg-[#503220] transition-colors duration-300 font-semibold touch-manipulation disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? 'Uploading…' : 'Upload'}
          </button>
        </div>
        </form>
      </div>
    </div>
  );
};

// ── Edit modal (super only) ───────────────────────────────────────────────────
const EditDocumentModal = ({ doc, onClose, onSaved, onError, allowed }) => {
  const existingAttachments = normalizePolicyTrainingAttachments(doc);
  const [documentNumber, setDocumentNumber] = useState(doc?.document_number || '');
  const [title, setTitle] = useState(doc?.title || '');
  const [description, setDescription] = useState(doc?.description || '');
  const [documentType, setDocumentType] = useState(doc?.document_type || 'policy');
  const [keptAttachments, setKeptAttachments] = useState(existingAttachments);
  const [removedIds, setRemovedIds] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [departments, setDepartments] = useState(
    Array.isArray(doc?.departments) ? doc.departments : [],
  );
  const [positions, setPositions] = useState(
    Array.isArray(doc?.positions) ? doc.positions : [],
  );
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState('');
  const fileInputRef = useRef(null);

  const remainingSlots = POLICY_TRAINING_MAX_FILES - keptAttachments.length;

  const handleAddFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (picked.length === 0) return;
    if (newFiles.length + picked.length > remainingSlots) {
      setFieldError(`Only ${remainingSlots} more file slot(s) available.`);
      return;
    }
    const combined = [...newFiles, ...picked];
    const error = validatePolicyTrainingFiles(combined);
    if (error) { setFieldError(error); return; }
    setFieldError('');
    setNewFiles(combined);
  };

  const removeExisting = (att) => {
    if (keptAttachments.length + newFiles.length <= 1 && newFiles.length === 0) {
      setFieldError('At least one file must remain attached.');
      return;
    }
    setFieldError('');
    setKeptAttachments((prev) => prev.filter((a) => a.id !== att.id));
    if (att.id) setRemovedIds((prev) => [...prev, att.id]);
  };

  const restoreRemoved = () => {
    setKeptAttachments(existingAttachments);
    setRemovedIds([]);
    setFieldError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allowed) {
      onError('Only admins can edit documents.');
      return;
    }
    if (!documentNumber.trim()) {
      setFieldError(`${numberFieldLabel(documentType)} is required.`);
      return;
    }
    if (!title.trim()) { setFieldError('Title is required.'); return; }
    if (keptAttachments.length === 0 && newFiles.length === 0) {
      setFieldError('At least one file must remain attached.');
      return;
    }
    setFieldError('');
    setSubmitting(true);
    try {
      const headers = await getAuthHeaders();
      const formData = new FormData();
      formData.append('document_number', documentNumber.trim());
      formData.append('title', title.trim());
      formData.append('description', description.trim());
      formData.append('document_type', documentType);
      formData.append('departments', departments.join(','));
      formData.append('positions', positions.join(','));
      if (removedIds.length > 0) {
        formData.append('removed_attachment_ids', removedIds.join(','));
      }
      newFiles.forEach((file) => formData.append('new_files', file, file.name));
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/policies-trainings/${doc.id}`,
        formData,
        { headers, validateStatus: (s) => s >= 200 && s < 300 },
      );
      onSaved('Document updated successfully!');
    } catch (err) {
      console.error('Update failed:', err);
      onError(parseApiErrorDetail(err) || 'Failed to update document. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!doc || !allowed) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 overflow-y-auto overscroll-contain py-4 px-3 sm:items-center sm:p-4">
      <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 max-w-lg w-full max-h-[min(90vh,calc(100dvh-2rem))] overflow-y-auto overscroll-contain min-w-0 my-auto">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Edit Document</h2>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              disabled={submitting}
            >
              {POLICY_TRAINING_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {numberFieldLabel(documentType)} *
            </label>
            <input
              type="text"
              value={documentNumber}
              onChange={(e) => setDocumentNumber(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              maxLength={50}
              disabled={submitting}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              disabled={submitting}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
              disabled={submitting}
            />
          </div>
          <DepartmentPositionScopePicker
            selectedDepartments={departments}
            selectedPositions={positions}
            onDepartmentsChange={setDepartments}
            onPositionsChange={setPositions}
            disabled={submitting}
            onLoadError={onError}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Attachments</label>
            <ul className="space-y-2 mb-2">
              {keptAttachments.map((att) => (
                <li key={att.id || att.file_url} className="flex items-center justify-between bg-gray-50 border rounded-lg px-3 py-2 text-sm">
                  <span className="truncate mr-2">{att.file_name || att.fileName}</span>
                  <button
                    type="button"
                    onClick={() => removeExisting(att)}
                    className="text-red-600 text-xs font-semibold shrink-0"
                    disabled={submitting}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            {removedIds.length > 0 && (
              <button type="button" onClick={restoreRemoved} className="text-xs text-[#674936] underline mb-2">
                Restore removed files
              </button>
            )}
            {remainingSlots > 0 && (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={POLICY_TRAINING_ACCEPT}
                  onChange={handleAddFiles}
                  className="hidden"
                  disabled={submitting}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={submitting}
                  className="px-3 py-1.5 text-sm bg-gray-100 border border-gray-300 rounded-lg"
                >
                  Add files
                </button>
              </div>
            )}
            {newFiles.length > 0 && (
              <ul className="space-y-2 mt-2">
                {newFiles.map((file, idx) => (
                  <li key={`new-${idx}`} className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 text-sm">
                    <span className="truncate mr-2">{file.name} (new)</span>
                    <button
                      type="button"
                      onClick={() => setNewFiles((prev) => prev.filter((_, i) => i !== idx))}
                      className="text-red-600 text-xs font-semibold shrink-0"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {fieldError && <p className="text-red-600 text-sm">{fieldError}</p>}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-semibold text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-[#674936] text-white rounded-lg font-semibold text-sm disabled:opacity-60"
            >
              {submitting ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────
const PoliciesTrainings = ({
  userProfile,
  pendingNotificationAction,
  onNotificationActionHandled,
}) => {
  const role = userProfile?.role;
  const canManage = canManagePolicyTraining(role);
  const canDelete = canDeletePolicyTraining(role);
  const canViewDeleted = canViewDeletedPolicyTraining(userProfile);
  const canRestore = canRestorePolicyTraining(userProfile);

  const [activeTab, setActiveTab] = useState('policy');
  const isDeletedTab = activeTab === 'deleted';
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [docToRestore, setDocToRestore] = useState(null);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  const [viewerAttachment, setViewerAttachment] = useState(null);
  const [popup, setPopup] = useState({ success: null, failure: null, info: null });

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    if (!pendingNotificationAction || pendingNotificationAction.type !== 'policy_training') {
      return undefined;
    }

    let cancelled = false;

    const openFromNotification = async () => {
      try {
        const headers = await getAuthHeaders();
        const doc = await fetchPolicyTrainingById(
          pendingNotificationAction.entityId,
          headers,
        );
        if (!cancelled) {
          const tab = ['training', 'hr_form'].includes(doc?.document_type)
            ? doc.document_type
            : 'policy';
          setActiveTab(tab);
          setSelectedDoc(doc);
        }
      } catch (err) {
        if (!cancelled) {
          setPopup({
            success: null,
            failure: null,
            info: parseNotificationActionError(err),
          });
        }
      } finally {
        if (!cancelled && onNotificationActionHandled) {
          onNotificationActionHandled();
        }
      }
    };

    openFromNotification();

    return () => {
      cancelled = true;
    };
  }, [pendingNotificationAction, onNotificationActionHandled]);

  const fetchDocuments = useCallback(async (page = 1, search = '', tab = activeTab) => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const params = {
        skip: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      };
      if (search.trim()) params.search = search.trim();

      let url = `${process.env.REACT_APP_BACKEND_URL}/api/policies-trainings`;
      if (tab === 'deleted') {
        url = `${process.env.REACT_APP_BACKEND_URL}/api/policies-trainings/deleted`;
      } else {
        params.document_type = tab;
      }

      const res = await axios.get(url, { headers, params });
      setDocuments(res.data.items || []);
      setTotalCount(res.data.total || 0);
    } catch (err) {
      console.error('Error fetching policies/trainings:', err);
      if (err.response?.status === 401) {
        setError('Authentication failed. Please sign in again.');
      } else if (err.response?.status === 403 && tab === 'deleted') {
        setError('Access denied. You do not have permission to view deleted documents.');
      } else {
        setError(parseApiErrorDetail(err) || 'Failed to load documents. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchDocuments(currentPage, debouncedSearch, activeTab);
  }, [currentPage, debouncedSearch, activeTab, fetchDocuments]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSelectedDoc(null);
    setShowRestoreConfirm(false);
    setDocToRestore(null);
    if (tab === 'deleted') {
      setShowUploadForm(false);
      setEditingDoc(null);
    }
  };

  const handleUploadSuccess = (message) => {
    setPopup({ success: message, failure: null, info: null });
    setCurrentPage(1);
    fetchDocuments(1, debouncedSearch, activeTab);
  };

  const handleEditSaved = (message) => {
    setEditingDoc(null);
    setSelectedDoc(null);
    setPopup({ success: message, failure: null, info: null });
    fetchDocuments(currentPage, debouncedSearch, activeTab);
  };

  useEffect(() => {
    if (!canManage) {
      setShowUploadForm(false);
      setEditingDoc(null);
    }
    if (!canDelete) {
      setShowDeleteConfirm(false);
      setDocToDelete(null);
    }
  }, [canManage, canDelete]);

  const handleEditFromDetail = (doc) => {
    if (!canManage) return;
    setSelectedDoc(null);
    setEditingDoc(doc);
  };

  const handleDeleteFromDetail = (doc) => {
    if (!canDelete || !doc?.id) return;
    setDocToDelete(doc.id);
    setShowDeleteConfirm(true);
    setSelectedDoc(null);
  };

  const confirmDeleteDocument = async () => {
    if (!docToDelete) return;
    try {
      const headers = await getAuthHeaders();
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/policies-trainings/${docToDelete}`,
        { headers },
      );
      setShowDeleteConfirm(false);
      setDocToDelete(null);
      setEditingDoc(null);
      setPopup({ success: 'Document moved to Deleted.', failure: null, info: null });
      const pageAfterDelete = documents.length <= 1 && currentPage > 1
        ? currentPage - 1
        : currentPage;
      if (pageAfterDelete !== currentPage) {
        setCurrentPage(pageAfterDelete);
      } else {
        fetchDocuments(currentPage, debouncedSearch, activeTab);
      }
    } catch (err) {
      console.error('Error deleting document:', err);
      if (err.response?.status === 403) {
        setPopup({ success: null, failure: 'Access denied. You do not have permission to delete this document.', info: null });
      } else if (err.response?.status === 404) {
        setPopup({ success: null, failure: 'Document not found. It may have already been deleted.', info: null });
      } else {
        setPopup({ success: null, failure: parseApiErrorDetail(err) || 'Failed to delete document. Please try again.', info: null });
      }
      setShowDeleteConfirm(false);
      setDocToDelete(null);
    }
  };

  const handleRestoreRequest = (documentId) => {
    if (!canRestore || !documentId) return;
    setDocToRestore(documentId);
    setShowRestoreConfirm(true);
  };

  const confirmRestoreDocument = async () => {
    if (!docToRestore) return;
    setRestoringId(docToRestore);
    try {
      const headers = await getAuthHeaders();
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/policies-trainings/${docToRestore}/restore`,
        {},
        { headers },
      );
      setShowRestoreConfirm(false);
      setDocToRestore(null);
      setPopup({ success: 'Document restored successfully!', failure: null, info: null });
      const pageAfterRestore = documents.length <= 1 && currentPage > 1
        ? currentPage - 1
        : currentPage;
      if (pageAfterRestore !== currentPage) {
        setCurrentPage(pageAfterRestore);
      } else {
        fetchDocuments(currentPage, debouncedSearch, activeTab);
      }
    } catch (err) {
      console.error('Error restoring document:', err);
      if (err.response?.status === 403) {
        setPopup({ success: null, failure: 'Access denied. You do not have permission to restore this document.', info: null });
      } else if (err.response?.status === 404) {
        setPopup({ success: null, failure: 'Deleted document not found or has expired.', info: null });
      } else {
        setPopup({ success: null, failure: parseApiErrorDetail(err) || 'Failed to restore document. Please try again.', info: null });
      }
      setShowRestoreConfirm(false);
      setDocToRestore(null);
    } finally {
      setRestoringId(null);
    }
  };

  const tabOptions = canViewDeleted ? [...TAB_OPTIONS, DELETED_TAB] : TAB_OPTIONS;

  const emptyMessage = () => {
    if (debouncedSearch) return 'No documents match your search.';
    if (activeTab === 'deleted') return 'No deleted documents.';
    if (activeTab === 'training') return 'No trainings have been uploaded yet.';
    if (activeTab === 'hr_form') return 'No HR forms have been uploaded yet.';
    return 'No policies have been uploaded yet.';
  };

  return (
    <div className="leave-management relative w-full min-w-0 max-w-full">
      <ReusablePopup
        success={popup.success}
        failure={popup.failure}
        info={popup.info}
        onClose={() => setPopup({ success: null, failure: null, info: null })}
      />

      <PolicyTrainingViewerModal
        attachment={viewerAttachment}
        onClose={() => setViewerAttachment(null)}
      />

      <DocumentDetailModal
        doc={selectedDoc}
        onClose={() => setSelectedDoc(null)}
        onEdit={handleEditFromDetail}
        onDelete={handleDeleteFromDetail}
        canEdit={canManage}
        canDelete={canDelete}
        role={role}
        onViewInApp={setViewerAttachment}
        onDownloadError={(msg) => setPopup({ success: null, failure: msg, info: null })}
      />

      {canDelete && showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 overflow-y-auto overscroll-contain py-4 px-3 sm:items-center sm:p-4">
          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 max-w-md w-full max-h-[min(90vh,calc(100dvh-2rem))] overflow-y-auto overscroll-contain min-w-0 my-auto">
            <div className="flex items-start gap-3 sm:gap-4">
              <span className="text-2xl shrink-0 text-red-600" aria-hidden="true">⚠️</span>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-gray-800 mb-4">Confirm Delete</h2>
                <p className="text-gray-600 mb-6 text-sm sm:text-base break-words">
                  Are you sure you want to delete this document? It will be moved to Deleted for 15 days. Only the designated restore account can recover it.
                </p>
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 w-full">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDocToDelete(null);
                }}
                className="w-full sm:w-auto bg-gray-500 text-white px-4 py-2.5 rounded-lg hover:bg-gray-600 transition-colors duration-300 font-semibold touch-manipulation"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteDocument}
                className="w-full sm:w-auto bg-red-500 text-white px-4 py-2.5 rounded-lg hover:bg-red-600 transition-colors duration-300 font-semibold touch-manipulation"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {canRestore && showRestoreConfirm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-50 overflow-y-auto overscroll-contain py-4 px-3 sm:items-center sm:p-4">
          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 max-w-md w-full max-h-[min(90vh,calc(100dvh-2rem))] overflow-y-auto overscroll-contain min-w-0 my-auto">
            <div className="flex items-start gap-3 sm:gap-4">
              <span className="text-2xl shrink-0 text-[#674936]" aria-hidden="true">↩</span>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-bold text-gray-800 mb-4">Confirm Restore</h2>
                <p className="text-gray-600 mb-6 text-sm sm:text-base break-words">
                  Restore this document? It will become visible again in Policies or Trainings.
                </p>
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 w-full">
              <button
                type="button"
                onClick={() => {
                  setShowRestoreConfirm(false);
                  setDocToRestore(null);
                }}
                className="w-full sm:w-auto bg-gray-500 text-white px-4 py-2.5 rounded-lg hover:bg-gray-600 transition-colors duration-300 font-semibold touch-manipulation"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmRestoreDocument}
                disabled={!!restoringId}
                className="w-full sm:w-auto bg-[#674936] text-white px-4 py-2.5 rounded-lg hover:bg-[#563a2e] transition-colors duration-300 font-semibold touch-manipulation disabled:opacity-60"
              >
                {restoringId ? 'Restoring…' : 'Restore'}
              </button>
            </div>
          </div>
        </div>
      )}

      {canManage && editingDoc && (
        <EditDocumentModal
          key={editingDoc.id}
          doc={editingDoc}
          onClose={() => setEditingDoc(null)}
          onSaved={handleEditSaved}
          onError={(msg) => setPopup({ success: null, failure: msg, info: null })}
          allowed={canManage}
        />
      )}

      {canManage && showUploadForm && (
        <UploadDocumentModal
          key={`upload-${activeTab}`}
          activeTab={activeTab}
          onClose={() => setShowUploadForm(false)}
          onSuccess={handleUploadSuccess}
          onError={(msg) => setPopup({ success: null, failure: msg, info: null })}
          allowed={canManage}
        />
      )}

      <div className="section-header bg-[#674936] text-white p-3 sm:p-4 rounded flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center !mb-4 sm:!mb-8">
        <h3 className="flex gap-2 sm:gap-4 text-white text-[1rem] md:text-[1.6rem] font-bold break-words min-w-0">
          Seazen Policies and Trainings
        </h3>
        <div className="flex items-center gap-3 shrink-0">
          {totalCount > 0 && (
            <span className="text-white text-sm opacity-80">
              {totalCount} item{totalCount !== 1 ? 's' : ''}
            </span>
          )}
          {canManage && !isDeletedTab && (
            <button
              type="button"
              onClick={() => setShowUploadForm(true)}
              className="w-full sm:w-auto shrink-0 bg-white text-[#674936] border-none px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg cursor-pointer text-sm transition-colors duration-300 font-semibold hover:bg-[#e2dbd6] touch-manipulation text-center"
            >
              Upload Document
            </button>
          )}
        </div>
      </div>

      {/* Policy / Training tabs */}
      <div className="mb-5 flex flex-wrap gap-2">
        {tabOptions.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => handleTabChange(tab.key)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors touch-manipulation ${
              activeTab === tab.key
                ? 'bg-[#674936] text-white'
                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="mb-5 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <div className="relative w-full sm:max-w-sm">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            placeholder="Search by number, title, description, or file name…"
            className="w-full border border-gray-300 rounded-lg pl-9 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#674936] focus:border-transparent"
          />
          <svg className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          {searchTerm && (
            <button
              type="button"
              onClick={() => { setSearchTerm(''); setCurrentPage(1); }}
              className="absolute right-2.5 top-2.5 text-gray-400 hover:text-gray-600 touch-manipulation"
              aria-label="Clear search"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : error ? (
        <div className="text-center py-12 text-red-600 text-sm">{error}</div>
      ) : documents.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <svg className="w-12 h-12 mx-auto mb-3 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-sm">{emptyMessage()}</p>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-3">
            {isDeletedTab
              ? documents.map((doc) => (
                <DeletedDocumentCard
                  key={doc.id || doc.original_document_id}
                  doc={doc}
                  onRestore={handleRestoreRequest}
                  restoring={restoringId === (doc.id || doc.original_document_id)}
                />
              ))
              : documents.map((doc) => (
                <DocumentCard
                  key={doc.id}
                  doc={doc}
                  onClick={setSelectedDoc}
                  showScope={canManage}
                />
              ))}
          </div>
          {totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                totalPages={totalPages}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PoliciesTrainings;
